import { useCallback, useEffect, useState } from 'react';

import { Template } from '../services/TemplateService/Template.type';
import { getTitle } from '../utils/templateUtils';
import { useBreakPoint } from './useBreakPoint';
import useCreateGameFromTemplate from './useCreateGameFromTemplate';
import useGameAutoSave from './useGameAutoSaver';
import { useMetrics } from './useMetrics';
import useRouterMatch from './useRouterMatch';

const usePresent = () => {
  const isDesktop = useBreakPoint(900);
  const metrics = useMetrics();
  const [isCreatingGameCurimotePressed, setIsCreatingGameCurimotePressed] = useState(
    false,
  );

  const { autoSave: autoSaveGame } = useGameAutoSave();
  const [isCreatingGamePresentPressed, setIsCreatingGamePresentPressed] = useState(false);
  const {
    createGameFromTemplate,
    isCreatingGameFromTemplate,
  } = useCreateGameFromTemplate();

  useEffect(() => {
    return () => {
      setIsCreatingGameCurimotePressed(false);
      setIsCreatingGamePresentPressed(false);
    };
  }, []);
  const { tenantId, router } = useRouterMatch();
  const openCurimote = useCallback(
    (gameId?: string | null) => {
      if (gameId) {
        const link = window.open(
          `${window.location.origin}/${tenantId}/games/${gameId}/curimote`,
          'curimote',
          `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
        );
        if (link) {
          link.opener = null;
        }
      }
    },
    [tenantId],
  );
  const routeToLive = useCallback(
    (gameId: string) => {
      router.push(`/${tenantId}/games/${gameId}/live`);
    },
    [router, tenantId],
  );

  const presentLesson = useCallback(
    async ({
      template,
      fromBeginning,
      method,
      gameId,
      callbackBeforeRedirect,
    }: {
      template: Template;
      gameId?: string | null;
      method: 'present' | 'curimote';
      fromBeginning?: boolean;
      callbackBeforeRedirect?: (gameId: string) => void;
    }) => {
      // private and workspace present
      if (template.content.slides && template.content.slides.length > 0) {
        if (method === 'curimote') setIsCreatingGameCurimotePressed(true);
        if (method === 'present') setIsCreatingGamePresentPressed(true);
        if (gameId) {
          // saves the first round as currentRound before routing;
          if (fromBeginning) {
            const title = getTitle({
              title: template.title,
              slides: template.content.slides,
            });
            await autoSaveGame({
              gameId,
              currentRound: template.content.slides[0].id,
              modifiedAt: Date.now(),
              isShowingLobby: true,
              slides: template.content.slides,
              title: title,
              attachments: template.attachments,
              requireRealNames: template.requireRealNames,
              isContentFilterEnabled: template.isContentFilterEnabled,
              settings: template.settings,
            });
            if (method === 'curimote' && isDesktop) {
              openCurimote(gameId);
            }
            return routeToLive(gameId);
          }

          // Routes the user to live;
          if (method === 'curimote' && isDesktop) {
            openCurimote(template.content.activeGame?.id);
          }
          return routeToLive(gameId);
        }

        // There is no game, and we need to create it before routing
        if (
          !template.content.activeGame ||
          (template.content.activeGame && !template.content.activeGame.id)
        ) {
          // Metric: Present attempted without an active game id
          metrics.logEvent('Creator.Present.NoActiveGame', {
            templateId: template.id,
          });
          const title = getTitle({
            title: template.title,
            slides: template.content.slides,
          });
          const roundToStartFrom = template.currentRound
            ? template.currentRound
            : template.content.slides?.[0].id;
          createGameFromTemplate({
            activeTenantId: tenantId,
            templateConfig: {
              title: title,
              id: template.id,
              slides: template.content.slides,
              attachments: template.attachments,
              metadata: template.metadata,
              modifiedAt: new Date().getTime(),
              requireRealNames: template.requireRealNames,
              isContentFilterEnabled: template.isContentFilterEnabled,
              currentRound: roundToStartFrom,
              settings: template.settings,
            },
            onSuccessFunction: (gameId: string) => {
              if (callbackBeforeRedirect) {
                callbackBeforeRedirect(gameId);
              }
              if (method === 'curimote' && isDesktop) {
                openCurimote(gameId);
              }
              return routeToLive(gameId);
            },
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoSaveGame,
      createGameFromTemplate,
      isDesktop,
      openCurimote,
      routeToLive,
      tenantId,
    ],
  );

  return {
    presentLesson,
    isCreatingGame:
      isCreatingGameFromTemplate ||
      isCreatingGameCurimotePressed ||
      isCreatingGamePresentPressed,
    isCreatingGameCurimote: isCreatingGameCurimotePressed,
    isCreatingGamePresent: isCreatingGamePresentPressed,
  };
};

export default usePresent;
